import React, { useState, useRef, useEffect } from 'react';
import { T, MONO, FONT, radius, tint, type as ty, alpha} from './poyais-ui/theme';
import { Card, Tile, Eyebrow, Badge, Button, Grid } from './poyais-ui/primitives';

// Gold gradient used for the primary CTAs — derived from the accent token so
// it stays in sync with the design system instead of a hardcoded hex pair.
const GOLD_GRADIENT = `linear-gradient(135deg, ${T.accent}, ${tint(T.accent, 45)})`;

// Template kategorileri ve şablonlar
const TEMPLATES = [
  { id:'t1', cat:'Ürün',      label:'Ürün Spotlight',    desc:'Beyaz arka plan, ürün odaklı.',        thumb:'https://images.unsplash.com/photo-1523275335684-37898b6baf30?w=400&q=80', prompt:'Product spotlight photography, clean white background, professional lighting, sharp details, commercial quality, centered composition', style:'photographic' },
  { id:'t2', cat:'Lifestyle', label:'Lifestyle Shot',    desc:'Doğal ortam, yaşam tarzı.',            thumb:'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=400&q=80', prompt:'Lifestyle product photography, natural environment, warm ambient lighting, authentic feel, person interacting with product, golden hour', style:'photographic' },
  { id:'t3', cat:'Lüks',      label:'Lüks & Premium',    desc:'Altın tonlar, premium his.',           thumb:'https://images.unsplash.com/photo-1563170351-be82bc888aa4?w=400&q=80', prompt:'Luxury product photography, gold and black tones, dramatic lighting, high-end aesthetic, elegant composition, premium feel, dark background', style:'photographic' },
  { id:'t4', cat:'Promosyon', label:'İndirim Afişi',     desc:'Renkli, dikkat çekici.',               thumb:'https://images.unsplash.com/photo-1607082348824-0a96f2a4b9da?w=400&q=80', prompt:'Sale promotion banner, vibrant colors, bold composition, eye-catching design, discount theme, energetic mood, bright background', style:'digital-art' },
  { id:'t5', cat:'Ürün',      label:'Flat Lay',          desc:'Yukarıdan, düzenli yerleşim.',         thumb:'https://images.unsplash.com/photo-1542291026-7eec264c27ff?w=400&q=80', prompt:'Flat lay product photography, top-down view, minimalist arrangement, pastel background, organized composition, soft shadows', style:'photographic' },
  { id:'t6', cat:'Yemek',     label:'Food Photography',  desc:'İştah açıcı, sıcak tonlar.',           thumb:'https://images.unsplash.com/photo-1565299624946-b28f40a0ae38?w=400&q=80', prompt:'Food photography, appetizing presentation, warm tones, bokeh background, steam effect, restaurant quality, close-up shot', style:'photographic' },
  { id:'t7', cat:'Teknoloji', label:'Tech Product',      desc:'Minimalist, modern his.',              thumb:'https://images.unsplash.com/photo-1468495244123-6c6c332eeece?w=400&q=80', prompt:'Tech product photography, minimalist dark background, blue accent lighting, modern aesthetic, clean lines, professional studio shot', style:'photographic' },
  { id:'t8', cat:'Moda',      label:'Fashion Editorial', desc:'Moda, şık kompozisyon.',               thumb:'https://images.unsplash.com/photo-1469334031218-e382a71b716b?w=400&q=80', prompt:'Fashion editorial photography, stylish composition, model with product, magazine quality, high contrast, professional lighting', style:'photographic' },
  { id:'t9', cat:'Lüks',      label:'Parfüm & Kozmetik', desc:'Çiçek, parfüm esintisi.',              thumb:'https://images.unsplash.com/photo-1592945403244-b3fbafd7f539?w=400&q=80', prompt:'Perfume cosmetic product photography, flowers and petals, soft pastel background, dreamy atmosphere, luxury feel, macro details', style:'photographic' },
  { id:'t10',cat:'Promosyon', label:'Sezonluk Kampanya', desc:'Kampanya temalı, hareketli.',          thumb:'https://images.unsplash.com/photo-1512909006721-3d6018887383?w=400&q=80', prompt:'Seasonal campaign product photography, festive atmosphere, colorful decorations, promotional feel, bright and cheerful, commercial quality', style:'digital-art' },
  { id:'t11',cat:'Ürün',      label:'Gradient BG',       desc:'Degrade arka plan, modern.',           thumb:'https://images.unsplash.com/photo-1491553895911-0055eca6402d?w=400&q=80', prompt:'Product photography on gradient background, smooth color transition, modern aesthetic, soft shadows, centered product, clean look', style:'photographic' },
  { id:'t12',cat:'Lifestyle', label:'Outdoor & Doğa',    desc:'Açık hava, doğal ışık.',               thumb:'https://images.unsplash.com/photo-1441716844725-09cedc13a4e7?w=400&q=80', prompt:'Outdoor lifestyle photography, natural light, green environment, product in nature, authentic atmosphere, golden hour lighting', style:'photographic' },
];

const FORMATS = [
  { key:'1:1',  label:'1:1',   desc:'Feed.',    w:1024, h:1024 },
  { key:'9:16', label:'9:16',  desc:'Story.',   w:768,  h:1344 },
  { key:'16:9', label:'16:9',  desc:'Banner.',  w:1344, h:768  },
  { key:'4:5',  label:'4:5',   desc:'Dikey.',   w:896,  h:1152 },
];

const STYLES = [
  { key:'photographic',  label:'Fotoğraf' },
  { key:'digital-art',   label:'Digital' },
  { key:'cinematic',     label:'Sinematik' },
  { key:'anime',         label:'Anime' },
  { key:'3d-model',      label:'3D' },
];

type View = 'home' | 'prompt' | 'template-detail';

const Spinner:React.FC=()=>(
  <span style={{display:'inline-block',width:16,height:16,border:`2.5px solid ${alpha(T.accent, 25)}`,borderTopColor:T.accent,borderRadius:'50%',animation:'spin 0.7s linear infinite'}}/>
);

const AIGorselTab:React.FC<{config?:any;creditBalance?:number;onSpend?:()=>void}>=({creditBalance:creditBalanceProp=0,onSpend})=>{
  const [view, setView]             = useState<View>('home');
  useEffect(()=>{
    const loadBalance = () => {
      const tok=localStorage.getItem('poyais_token')||localStorage.getItem('token')||'';
      if(!tok) return;
      fetch('/api/v1/credits/balance',{headers:{'Authorization':'Bearer '+tok}})
        .then(r=>r.ok?r.json():Promise.reject(r.status))
        .then(d=>{ void d; });
    };
    loadBalance();
  },[]);
  const [selectedTemplate, setST]   = useState<typeof TEMPLATES[0]|null>(null);
  const [prompt, setPrompt]         = useState('');
  const [format, setFormat]         = useState('1:1');
  const [style, setStyle]           = useState('photographic');
  const [imgMode, setImgMode]       = useState<'text'|'image'>('text');
  const [uploadedImg, setUploadedImg]         = useState<File|null>(null);
  const [uploadedImgPreview, setUIP]          = useState<string|null>(null);
  const [loading, setLoading]       = useState(false);
  const [result, setResult]         = useState<string|null>(null);
  const [error, setError]           = useState<string|null>(null);
  const [productDesc, setProductDesc] = useState('');
  const [creditBalance, setCreditBalance] = useState(creditBalanceProp);
  const fileRef = useRef<HTMLInputElement>(null);

  const token = () => localStorage.getItem('poyais_token') || '';

  const buildPrompt = () => {
    if (selectedTemplate) {
      return productDesc.trim()
        ? `${selectedTemplate.prompt}. Product: ${productDesc}`
        : selectedTemplate.prompt;
    }
    return prompt;
  };

  const generate = async () => {
    const finalPrompt = buildPrompt();
    if (!finalPrompt.trim()) return;
    if (imgMode === 'image' && !uploadedImg) return;
    if (creditBalance < 5) {
      setError('Yetersiz kredi. Görsel üretimi 5 kredi gerektirir. Lütfen kredi yükleyin.');
      return;
    }

    setLoading(true); setResult(null); setError(null);
    try {
      let res: Response;
      if (imgMode === 'image' && uploadedImg) {
        const fd = new FormData();
        fd.append('prompt', finalPrompt);
        fd.append('format', format);
        fd.append('style_preset', selectedTemplate?.style || style);
        fd.append('image', uploadedImg);
        res = await fetch('/api/v1/creative/generate-image-from-image', {
          method: 'POST',
          headers: { 'Authorization': 'Bearer ' + token() },
          body: fd,
        });
      } else {
        res = await fetch('/api/v1/creative/generate-image', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + token() },
          body: JSON.stringify({ prompt: finalPrompt, format, style_preset: selectedTemplate?.style || style }),
        });
      }
      const data = await res.json();
      if (data.url) { setResult(data.url); setCreditBalance(b=>Math.max(0,b-5)); if(onSpend) onSpend(); }
      else setError(data.detail || 'Görsel üretilemedi.');
    } catch {
      setError('Bir hata oluştu.');
    }
    setLoading(false);
  };

  const selectTemplate = (t: typeof TEMPLATES[0]) => {
    setST(t);
    setStyle(t.style);
    setView('template-detail');
    setResult(null);
    setError(null);
  };

  const goHome = () => {
    setView('home');
    setResult(null);
    setError(null);
    setST(null);
    setPrompt('');
  };

  // ── HOME: şablon galerisi ──────────────────────────────────────────────
  if (view === 'home') return (
    <div style={{fontFamily:FONT,minHeight:'100vh',background:T.surface}}>
      {/* Kredi göstergesi */}
      <div style={{display:'flex',justifyContent:'flex-end',alignItems:'center',gap:10,padding:'12px 16px 0'}}>
        <Badge color={T.accent}>
          <span style={{...ty.label,color:T.accentDeep,letterSpacing:'0.08em'}}>KREDİ</span>
          <span style={{fontFamily:MONO,fontWeight:800}}>{creditBalance}</span>
        </Badge>
        <Button variant="soft" color={T.accent} onClick={()=>{ (window as any).__poyaisGoNav?.('kredi-yukle'); }}>
          Kredi Yükle
        </Button>
      </div>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}@keyframes fadeIn{from{opacity:0;transform:translateY(6px)}to{opacity:1;transform:none}}`}</style>

      {/* Hero */}
      <div style={{padding:'24px 16px 20px',borderBottom:`1px solid ${T.border}`}}>
        <div style={{maxWidth:600,margin:'0 auto',textAlign:'center' as const}}>
          <Eyebrow color={T.accent}>AI Görsel Üretici</Eyebrow>
          <h2 style={{fontSize:26,fontWeight:800,color:T.text,margin:'0 0 8px',letterSpacing:'-0.02em'}}>Ne üretmek istersiniz?</h2>
          <p style={{fontSize:13,color:T.textMid,margin:'0 0 20px'}}>Şablon seçin veya sıfırdan oluşturun.</p>
          <div style={{display:'flex',gap:10,justifyContent:'center'}}>
            <Button variant="solid" color={T.accent} onClick={()=>{setView('prompt');setST(null);setImgMode('text');}}
              style={{padding:'10px 24px',background:GOLD_GRADIENT,border:`1px solid ${T.accent}`,color:'#1a1000'}}>
              Promptla Üret
            </Button>
            <Button variant="outline" color={T.accent} onClick={()=>{setView('prompt');setST(null);setImgMode('image');}}
              style={{padding:'10px 24px'}}>
              Görselden Üret
            </Button>
          </div>
        </div>
      </div>

      {/* Şablon galerisi */}
      <div style={{padding:'0 16px'}}>
        <Grid min={200} gap={16} style={{paddingBottom:32}}>
          {TEMPLATES.map(t=>(
            <Card key={t.id} hover pad={0} onClick={()=>selectTemplate(t)}
              style={{overflow:'hidden',cursor:'pointer',animation:'fadeIn 0.2s ease'}}>
              {/* Şablon önizleme */}
              <div style={{height:160,background:T.card,overflow:'hidden',position:'relative'}}>
                <img src={t.thumb} alt={t.label}
                  style={{width:'100%',height:'100%',objectFit:'cover' as const,display:'block'}}/>
                <div style={{position:'absolute',top:8,left:8}}>
                  <Badge color={T.accent}>{t.cat}</Badge>
                </div>
              </div>
              <div style={{padding:'12px 14px'}}>
                <div style={{fontSize:13,fontWeight:700,color:T.text,marginBottom:3}}>{t.label}</div>
                <div style={{fontSize:11,color:T.textMid}}>{t.desc}</div>
              </div>
            </Card>
          ))}
        </Grid>
      </div>
    </div>
  );

  // ── TEMPLATE DETAIL / PROMPT VIEW ────────────────────────────────────
  return (
    <div style={{fontFamily:FONT,background:T.surface,minHeight:'100vh'}}>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}@keyframes fadeIn{from{opacity:0}to{opacity:1}}`}</style>

      {/* Header */}
      <div style={{padding:'12px 16px',borderBottom:`1px solid ${T.border}`,display:'flex',alignItems:'center',gap:12}}>
        <Button variant="ghost" onClick={goHome} style={{padding:'6px 14px'}}>
          ← Geri
        </Button>
        <span style={{fontSize:15,fontWeight:700,color:T.text}}>
          {selectedTemplate ? selectedTemplate.label : imgMode==='image' ? 'Görselden Üret' : 'Promptla Üret'}
        </span>
      </div>

      <div style={{padding:'16px',display:'flex',flexDirection:'column' as const,gap:20,maxWidth:1100}}>

        {/* Sol form */}
        <div style={{display:'flex',flexDirection:'column' as const,gap:14,width:'100%'}}>

          {/* Mod seçici — sadece prompt view'da göster */}
          {!selectedTemplate&&(
            <div style={{display:'flex',background:T.card,borderRadius:radius.md,padding:3,border:`1px solid ${alpha(T.accent, 13)}`}}>
              {([{key:'text',label:'Metinden'},{key:'image',label:'Görselden'}] as const).map(m=>(
                <Button key={m.key} variant={imgMode===m.key?'outline':'ghost'} color={T.accent}
                  onClick={()=>setImgMode(m.key)}
                  style={{flex:1,padding:'8px',borderRadius:6,
                    border:imgMode===m.key?`1px solid ${alpha(T.accent, 25)}`:'1px solid transparent',
                    background:imgMode===m.key?T.surface:'transparent'}}>
                  {m.label}
                </Button>
              ))}
            </div>
          )}

          {/* Görsel yükleme */}
          {(imgMode==='image'||selectedTemplate)&&(
            <Card pad={16}>
              <Eyebrow>{selectedTemplate?'Ürün Görseli (Opsiyonel)':'Referans Görsel'}</Eyebrow>
              <div onClick={()=>fileRef.current?.click()}
                style={{padding:'14px',border:`1.5px dashed ${uploadedImg?`${alpha(T.accent, 25)}`:`${alpha(T.accent, 15)}`}`,
                  borderRadius:radius.md,textAlign:'center' as const,cursor:'pointer',
                  background:uploadedImg?tint(T.accent,10):T.card,transition:'all .2s'}}>
                {uploadedImgPreview?(
                  <div>
                    <img src={uploadedImgPreview} alt="" style={{maxHeight:100,borderRadius:6,marginBottom:4,display:'block',margin:'0 auto 4px'}}/>
                    <div style={{fontSize:10,color:T.textMid}}>Değiştirmek için tıkla</div>
                  </div>
                ):(
                  <div>
                    <div style={{fontSize:22,marginBottom:4}}>🖼️</div>
                    <div style={{fontSize:12,color:T.text,fontWeight:600}}>Görsel yükle</div>
                    <div style={{fontSize:10,color:T.textMid,marginTop:2}}>JPG / PNG / WebP</div>
                  </div>
                )}
              </div>
              <input ref={fileRef} type="file" accept="image/*" style={{display:'none'}}
                onChange={e=>{
                  const f=e.target.files?.[0];
                  if(!f)return;
                  setUploadedImg(f);
                  const r=new FileReader();
                  r.onload=ev=>setUIP(ev.target?.result as string);
                  r.readAsDataURL(f);
                }}/>
            </Card>
          )}

          {/* Ürün açıklaması (template mode) */}
          {selectedTemplate&&(
            <Card pad={16}>
              <Eyebrow>Ürün / Marka Açıklaması</Eyebrow>
              <textarea value={productDesc} onChange={e=>setProductDesc(e.target.value)}
                placeholder="Örn: ZsaZsa nevresim takımı, premium pamuk, krem rengi…"
                rows={3} style={{width:'100%',background:T.card,border:`1px solid ${T.border}`,
                  borderRadius:radius.sm,color:T.text,fontSize:12,padding:'9px 12px',resize:'vertical' as const,
                  outline:'none',fontFamily:'inherit',lineHeight:1.6,boxSizing:'border-box' as const}}
                onFocus={e=>e.target.style.borderColor=T.accent}
                onBlur={e=>e.target.style.borderColor=T.border}/>
            </Card>
          )}

          {/* Prompt (sadece prompt mode) */}
          {!selectedTemplate&&(
            <Card pad={16}>
              <Eyebrow>Görsel Promptu</Eyebrow>
              <textarea value={prompt} onChange={e=>setPrompt(e.target.value)}
                placeholder="Görseli tanımlayın…"
                rows={4} style={{width:'100%',background:T.card,border:`1px solid ${T.border}`,
                  borderRadius:radius.sm,color:T.text,fontSize:12,padding:'9px 12px',resize:'vertical' as const,
                  outline:'none',fontFamily:'inherit',lineHeight:1.6,boxSizing:'border-box' as const}}
                onFocus={e=>e.target.style.borderColor=T.accent}
                onBlur={e=>e.target.style.borderColor=T.border}/>
            </Card>
          )}

          {/* Format */}
          <Tile pad={16}>
            <Eyebrow>Format</Eyebrow>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:6,marginTop:10}}>
              {FORMATS.map(f=>(
                <Button key={f.key} variant={format===f.key?'soft':'outline'} color={T.accent}
                  onClick={()=>setFormat(f.key)}
                  style={{padding:'8px',justifyContent:'space-between',width:'100%'}}>
                  <span style={{fontSize:12,fontWeight:700}}>{f.label}</span>
                  <span style={{fontSize:10,color:T.textDim}}>{f.desc}</span>
                </Button>
              ))}
            </div>
          </Tile>

          {/* Stil */}
          {!selectedTemplate&&(
            <Tile pad={16}>
              <Eyebrow>Stil</Eyebrow>
              <div style={{display:'flex',flexWrap:'nowrap' as const,gap:4,overflowX:'auto' as const,scrollbarWidth:'none' as const,msOverflowStyle:'none' as const,marginTop:10}}>
                {STYLES.map(s=>(
                  <Button key={s.key} variant={style===s.key?'soft':'outline'} color={T.accent}
                    onClick={()=>setStyle(s.key)}
                    style={{padding:'4px 10px',fontSize:11,borderRadius:radius.pill,flexShrink:0}}>
                    {s.label}
                  </Button>
                ))}
              </div>
            </Tile>
          )}

          <Button variant="solid" color={T.accent} onClick={generate}
            disabled={loading||(!selectedTemplate&&!prompt.trim()&&imgMode==='text')||(imgMode==='image'&&!selectedTemplate&&!uploadedImg)}
            style={{width:'100%',padding:'12px',fontSize:13,fontWeight:800,
              background:loading?T.card:GOLD_GRADIENT,
              border:`1px solid ${loading?`${alpha(T.accent, 15)}`:T.accent}`,
              color:loading?T.textMid:'#1a1000',cursor:loading?'not-allowed':'pointer'}}>
            {loading?<><Spinner/><span>Üretiliyor…</span></>:<span>Görsel Üret</span>}
          </Button>
        </div>

        {/* Sonuç — butonun altında */}
        <div style={{width:'100%'}}>
          {error&&(
            <div style={{background:tint(T.red,6),border:`1px solid ${alpha(T.red, 20)}`,
              borderRadius:radius.md,padding:'14px 16px',marginBottom:14,fontSize:12,color:T.redText}}>
              ❌ {error}
            </div>
          )}

          {loading&&!result&&(
            <Card style={{padding:'64px 24px',textAlign:'center' as const}}>
              <div style={{marginBottom:16,display:'flex',justifyContent:'center'}}><Spinner/></div>
              <div style={{fontSize:14,fontWeight:700,color:T.text,marginBottom:6}}>Görsel üretiliyor…</div>
              <div style={{fontSize:12,color:T.textMid}}>Stability AI işliyor, birkaç saniye…</div>
            </Card>
          )}

          {result&&(
            <div style={{animation:'fadeIn 0.3s ease'}}>
              <img src={result} alt="AI Görsel"
                style={{width:'100%',borderRadius:radius.lg,display:'block',
                  maxHeight:600,objectFit:'contain' as const,background:T.card}}/>
              <div style={{display:'flex',gap:10,marginTop:12}}>
                <a href={result} download="poyais-gorsel.png"
                  style={{flex:1,padding:'10px',textAlign:'center' as const,
                    background:GOLD_GRADIENT,
                    border:`1px solid ${T.accent}`,borderRadius:radius.sm,color:'#1a1000',
                    fontSize:12,fontWeight:700,textDecoration:'none'}}>
                  ⬇ İndir
                </a>
                <Button variant="soft" color={T.accent} onClick={generate} style={{flex:1}}>
                  ↺ Yeniden Üret
                </Button>
                <Button variant="ghost" onClick={()=>{setResult(null);}} style={{padding:'10px 16px'}}>
                  ✕
                </Button>
              </div>
            </div>
          )}

          {!loading&&!result&&!error&&(
            <Card style={{padding:'64px 24px',textAlign:'center' as const}}>
              <div style={{fontSize:48,marginBottom:16}}>🖼️</div>
              <div style={{fontSize:16,fontWeight:700,color:T.text,marginBottom:8}}>
                {selectedTemplate?`${selectedTemplate.label} Şablonu`:'Görsel Üretici'}
              </div>
              <div style={{fontSize:13,color:T.textMid,lineHeight:1.7}}>
                {selectedTemplate
                  ? 'Ürün açıklamasını girin ve görseli üretin.'
                  : 'Promptu yazın, formatı seçin ve görseli üretin.'}
              </div>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default AIGorselTab;
